import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowUpRight,
  Banknote,
  Check,
  CheckCircle2,
  Loader2,
  Search,
  StickyNote,
  Truck,
  Wrench,
  X,
} from 'lucide-react';
import { assistantAPI } from '../../lib/api';
import { useDebouncedValue } from '../../hooks/use-debounced-value';
import { cn } from '../../lib/utils';
import { messageErreur } from '../../lib/erreurs';

/**
 * Brouillon de saisie proposé par l'assistant (spec 043): panne, note ou frais.
 *
 * Tout se corrige ici avant d'être envoyé; rien n'est écrit tant que
 * l'utilisateur n'a pas touché « Confirmer ». Le serveur contrôle tout de
 * nouveau (camion du parc, trajet du camion, choix, longueurs) et la clé du
 * brouillon rend un double clic sans effet.
 */

const APPARENCE = {
  panne: { icone: Wrench, teinte: 'bg-flame/10 text-flame-ink' },
  note: { icone: StickyNote, teinte: 'bg-primary/10 text-primary' },
  frais: { icone: Banknote, teinte: 'bg-success/10 text-success' },
};

const GRAVITE_TEINTE = {
  1: 'data-[actif=true]:border-success/60 data-[actif=true]:bg-success/10',
  2: 'data-[actif=true]:border-flame/60 data-[actif=true]:bg-flame/10',
  3: 'data-[actif=true]:border-destructive/60 data-[actif=true]:bg-destructive/10',
};

export const valeursInitiales = (draft) => ({
  truck: draft.truck || null,
  trip_id: draft.trip_id || '',
  ...Object.fromEntries(Object.entries(draft.fields || {}).map(([cle, valeur]) => [cle, valeur ?? ''])),
});

const Champ = ({ label, htmlFor, children, aide }) => (
  <div className="space-y-1.5">
    <label htmlFor={htmlFor} className="block text-xs font-medium text-muted-foreground">
      {label}
    </label>
    {children}
    {aide ? <p className="text-[11px] text-muted-foreground">{aide}</p> : null}
  </div>
);

const Segments = ({ id, options, valeur, onChange, teintes, disabled }) => (
  <div id={id} role="radiogroup" className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
    {options.map((option) => {
      const actif = String(valeur) === String(option.value);
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={actif}
          data-actif={actif}
          disabled={disabled}
          onClick={() => onChange(option.value)}
          title={option.label}
          className={cn(
            'min-h-[2.5rem] rounded-xl border border-border bg-card px-2 py-1.5 text-xs font-medium text-muted-foreground transition-colors',
            'hover:border-primary/40 hover:text-foreground',
            'data-[actif=true]:border-primary/60 data-[actif=true]:bg-primary/5 data-[actif=true]:text-foreground',
            teintes?.[option.value],
          )}
        >
          {option.label.split(' :')[0]}
        </button>
      );
    })}
  </div>
);

function ChoixCamion({ draftId, camion, requete, candidats, onChoisir, disabled }) {
  const [saisie, setSaisie] = useState(requete || '');
  const [resultats, setResultats] = useState(candidats || []);
  const [cherche, setCherche] = useState(false);
  const terme = useDebouncedValue(saisie, 300);
  const premierRendu = useRef(true);

  useEffect(() => {
    if (premierRendu.current) {
      premierRendu.current = false;
      if (candidats?.length) return undefined;
    }
    if (camion || terme.trim().replace(/\s/g, '').length < 2) {
      setResultats([]);
      return undefined;
    }
    let annule = false;
    setCherche(true);
    assistantAPI
      .searchTrucks(terme.trim())
      .then((trouves) => {
        if (!annule) setResultats(Array.isArray(trouves) ? trouves : []);
      })
      .catch(() => {
        if (!annule) setResultats([]);
      })
      .finally(() => {
        if (!annule) setCherche(false);
      });
    return () => {
      annule = true;
    };
  }, [terme, camion, candidats]);

  if (camion) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/50 px-3 py-2">
        <Truck className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <span className="flex-1 text-sm font-semibold tracking-wide text-foreground">{camion.label}</span>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChoisir(null)}
          className="rounded-lg px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10"
        >
          Changer
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <input
          id={`${draftId}-camion`}
          className="input-base pl-9"
          placeholder="Plaque du camion (ex : AB-123-CD)"
          value={saisie}
          disabled={disabled}
          autoComplete="off"
          onChange={(e) => setSaisie(e.target.value)}
        />
        {cherche ? (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" aria-hidden="true" />
        ) : null}
      </div>
      {resultats.length ? (
        <div className="flex flex-wrap gap-1.5">
          {resultats.map((trouve) => (
            <button
              key={trouve.id}
              type="button"
              disabled={disabled}
              onClick={() => onChoisir(trouve)}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold tracking-wide text-foreground transition-colors hover:border-primary/50 hover:bg-primary/5"
            >
              <Truck className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
              {trouve.label}
            </button>
          ))}
        </div>
      ) : null}
      {!cherche && saisie.trim().length >= 2 && !resultats.length ? (
        <p className="text-[11px] text-muted-foreground">Aucun camion du parc ne correspond.</p>
      ) : null}
    </div>
  );
}

const pret = (flow, v, trajets) => {
  if (!v.truck) return false;
  const besoinTrajet = flow === 'frais' || (flow === 'note' && v.target === 'trip');
  if (besoinTrajet && !v.trip_id) return false;
  if (flow === 'panne') return Boolean(v.category && v.severity && (v.description || '').trim().length >= 5);
  if (flow === 'note') return Boolean(v.kind && (v.text || '').trim().length >= 3);
  if (flow === 'frais') {
    const trajet = trajets.find((t) => t.id === v.trip_id);
    return Boolean(v.kind && String(v.amount || '').trim() && (v.payer || (trajet && !trajet.has_driver)));
  }
  return false;
};

export function AssistantDraftCard({ draft, etat, onEtat, onLien }) {
  const id = `brouillon-${etat.key}`;
  const valeurs = etat.values;
  const [trajets, setTrajets] = useState(draft.trips || []);
  const enCours = etat.status === 'saving';
  const termine = etat.status === 'done';
  const abandonne = etat.status === 'cancelled';
  const fige = enCours || termine || abandonne;
  const { icone: Icone, teinte } = APPARENCE[draft.flow] || APPARENCE.note;
  const besoinTrajet = draft.flow === 'frais' || (draft.flow === 'note' && valeurs.target === 'trip');

  const changer = (champs) => onEtat({ ...etat, values: { ...valeurs, ...champs }, error: null });

  useEffect(() => {
    if (!valeurs.truck?.id || !besoinTrajet || fige) return undefined;
    if (trajets.length && draft.truck?.id === valeurs.truck.id) return undefined;
    let annule = false;
    assistantAPI
      .truckTrips(valeurs.truck.id)
      .then((liste) => {
        if (annule) return;
        const trouves = Array.isArray(liste) ? liste : [];
        setTrajets(trouves);
        if (trouves.length === 1 && !valeurs.trip_id) changer({ trip_id: trouves[0].id });
      })
      .catch(() => {
        if (!annule) setTrajets([]);
      });
    return () => {
      annule = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valeurs.truck?.id, besoinTrajet]);

  const confirmer = async () => {
    onEtat({ ...etat, status: 'saving', error: null });
    const corps = {
      key: etat.key,
      truck_id: valeurs.truck?.id,
      trip_id: besoinTrajet ? valeurs.trip_id || null : null,
      ...Object.fromEntries(
        ['category', 'severity', 'description', 'target', 'kind', 'text', 'amount', 'payer']
          .filter((cle) => valeurs[cle] !== undefined && valeurs[cle] !== '')
          .map((cle) => [cle, String(valeurs[cle])]),
      ),
    };
    try {
      const resultat = await assistantAPI.confirmDraft(draft.flow, corps);
      onEtat({ ...etat, status: 'done', result: resultat, error: null });
    } catch (e) {
      const message = messageErreur(e, 'L’enregistrement a échoué. Rien n’a été enregistré.');
      onEtat({ ...etat, status: 'editing', error: message });
    }
  };

  if (termine) {
    return (
      <div className="mt-2 flex items-start gap-3 rounded-2xl border border-success/30 bg-success/10 p-3">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden="true" />
        <div className="min-w-0 space-y-2">
          <p className="text-sm font-medium text-foreground">{etat.result?.text?.replace(/^✅\s*/, '')}</p>
          {etat.result?.link ? (
            <button
              type="button"
              onClick={() => onLien(etat.result.link.to)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
            >
              {etat.result.link.label}
              <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (abandonne) {
    return (
      <p className="mt-2 rounded-2xl border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
        {draft.title} — saisie abandonnée, rien n’a été enregistré.
      </p>
    );
  }

  const options = draft.options || {};
  const pretAEnvoyer = pret(draft.flow, valeurs, trajets);

  return (
    <section
      aria-labelledby={`${id}-titre`}
      className="mt-2 overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
    >
      <header className="flex items-center gap-3 border-b border-border/70 px-3.5 py-3">
        <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', teinte)}>
          <Icone className="h-[18px] w-[18px]" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 id={`${id}-titre`} className="truncate text-sm font-semibold text-foreground">
            {draft.title}
          </h3>
          <p className="text-[11px] text-muted-foreground">Brouillon · à vérifier, puis confirmer</p>
        </div>
      </header>

      <div className="space-y-3 px-3.5 py-3">
        <Champ label="Camion" htmlFor={`${id}-camion`}>
          <ChoixCamion
            draftId={id}
            camion={valeurs.truck}
            requete={draft.truck_query}
            candidats={draft.candidates}
            disabled={fige}
            onChoisir={(camion) => {
              setTrajets([]);
              changer({ truck: camion, trip_id: '' });
            }}
          />
        </Champ>

        {draft.flow === 'note' ? (
          <Champ label="La note porte sur" htmlFor={`${id}-cible`}>
            <Segments
              id={`${id}-cible`}
              options={options.target || []}
              valeur={valeurs.target}
              disabled={fige}
              onChange={(target) => changer({ target, trip_id: '' })}
            />
          </Champ>
        ) : null}

        {besoinTrajet && valeurs.truck ? (
          <Champ label="Trajet" htmlFor={`${id}-trajet`}>
            {trajets.length ? (
              <select
                id={`${id}-trajet`}
                className="input-base"
                value={valeurs.trip_id}
                disabled={fige}
                onChange={(e) => changer({ trip_id: e.target.value })}
              >
                <option value="">Choisir le trajet…</option>
                {trajets.map((trajet) => (
                  <option key={trajet.id} value={trajet.id}>{trajet.label}</option>
                ))}
              </select>
            ) : (
              <p className="rounded-xl bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
                Aucun trajet en cours pour ce camion.
              </p>
            )}
          </Champ>
        ) : null}

        {draft.flow === 'panne' ? (
          <>
            <Champ label="Problème" htmlFor={`${id}-categorie`}>
              <select
                id={`${id}-categorie`}
                className="input-base"
                value={valeurs.category}
                disabled={fige}
                onChange={(e) => changer({ category: e.target.value })}
              >
                <option value="">Choisir…</option>
                {(options.category || []).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </Champ>
            <Champ
              label="Gravité"
              htmlFor={`${id}-gravite`}
              aide={String(valeurs.severity) === '3' ? 'Le camion sera immobilisé dès l’enregistrement.' : null}
            >
              <Segments
                id={`${id}-gravite`}
                options={options.severity || []}
                valeur={valeurs.severity}
                teintes={GRAVITE_TEINTE}
                disabled={fige}
                onChange={(severity) => changer({ severity })}
              />
            </Champ>
            <Champ label="Description" htmlFor={`${id}-description`}>
              <textarea
                id={`${id}-description`}
                className="input-base h-auto min-h-[4.5rem] resize-none py-2 leading-relaxed"
                maxLength={500}
                value={valeurs.description}
                disabled={fige}
                placeholder="Ce qui s’est passé, en une ou deux phrases"
                onChange={(e) => changer({ description: e.target.value })}
              />
            </Champ>
          </>
        ) : null}

        {draft.flow === 'note' ? (
          <>
            <Champ label="Sorte de note" htmlFor={`${id}-sorte`}>
              <select
                id={`${id}-sorte`}
                className="input-base"
                value={valeurs.kind}
                disabled={fige}
                onChange={(e) => changer({ kind: e.target.value })}
              >
                <option value="">Choisir…</option>
                {(options.kind || []).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </Champ>
            <Champ label="Note" htmlFor={`${id}-texte`}>
              <textarea
                id={`${id}-texte`}
                className="input-base h-auto min-h-[4.5rem] resize-none py-2 leading-relaxed"
                maxLength={1000}
                value={valeurs.text}
                disabled={fige}
                onChange={(e) => changer({ text: e.target.value })}
              />
            </Champ>
          </>
        ) : null}

        {draft.flow === 'frais' ? (
          <>
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <Champ label="Montant" htmlFor={`${id}-montant`}>
                <div className="relative">
                  <input
                    id={`${id}-montant`}
                    className="input-base pr-14 text-base font-semibold tabular-nums"
                    inputMode="decimal"
                    value={valeurs.amount}
                    disabled={fige}
                    placeholder="12 500"
                    onChange={(e) => changer({ amount: e.target.value })}
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-muted-foreground">
                    {draft.currency || 'FCFA'}
                  </span>
                </div>
              </Champ>
            </div>
            <Champ label="Sorte de frais" htmlFor={`${id}-sorte`}>
              <select
                id={`${id}-sorte`}
                className="input-base"
                value={valeurs.kind}
                disabled={fige}
                onChange={(e) => changer({ kind: e.target.value })}
              >
                <option value="">Choisir…</option>
                {(options.kind || []).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </Champ>
            <Champ label="Payé par" htmlFor={`${id}-payeur`}>
              <Segments
                id={`${id}-payeur`}
                options={(options.payer || []).map((option) => ({
                  ...option,
                  label: option.value === '1' ? 'Le chauffeur' : 'La caisse',
                }))}
                valeur={valeurs.payer}
                disabled={fige}
                onChange={(payer) => changer({ payer })}
              />
            </Champ>
          </>
        ) : null}

        {etat.error ? (
          <p role="alert" className="rounded-xl bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive">
            {etat.error}
          </p>
        ) : null}
      </div>

      <footer className="flex items-center gap-2 border-t border-border/70 bg-muted/30 px-3.5 py-2.5">
        <button
          type="button"
          disabled={enCours}
          onClick={() => onEtat({ ...etat, status: 'cancelled' })}
          className="inline-flex h-10 items-center justify-center gap-1 rounded-xl px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" aria-hidden="true" />
          Annuler
        </button>
        <button
          type="button"
          disabled={!pretAEnvoyer || enCours}
          onClick={confirmer}
          title={pretAEnvoyer ? undefined : 'Complétez le brouillon pour confirmer'}
          className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {enCours ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Check className="h-4 w-4" aria-hidden="true" />
          )}
          Confirmer
        </button>
      </footer>
    </section>
  );
}

export default AssistantDraftCard;
